import React, { useEffect, useRef, useState } from "react";
import { searchFreightLocations } from "../../utils/api";

/**
 * Admin freight profiling — aligned with `POST/GET …/profiling` + `freight-profiling/location-search`.
 * Saves `loading.rows` / `unloading.rows` as `{ zone_id, state_id, city_ids }` (0 = all; `[0]` = all cities in state).
 */

/** API `bucket` → internal search row kind. */
const API_BUCKET_TO_INTERNAL = {
  city: "cities",
  state: "states",
  zone: "zones"
};

const INTERNAL_KIND_LABEL = {
  cities: "City",
  states: "State",
  zones: "Zone"
};

export const LOCATION_GROUP_KIND = "location_group";

/** Search `types` query fragment per tier (backend accepts comma lists; single bucket is enough here). */
function typesParamForTier(tier) {
  if (tier === "zone") return "zones";
  if (tier === "state") return "states";
  return "cities";
}

export function num(v) {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function inferBucketFromRow(row) {
  if (num(row.city_id) != null) return "cities";
  if (num(row.state_id) != null) return "states";
  if (num(row.zone_id) != null) return "zones";
  return null;
}

export function isLocationGroup(s) {
  return s && s.kind === LOCATION_GROUP_KIND;
}

export function buildGroupLabel(zoneLabel, stateLabel, citySummary) {
  return [zoneLabel, stateLabel, citySummary].filter(Boolean).join(" → ");
}

function normalizeCityIdsFromApi(loc) {
  if (!loc || typeof loc !== "object") return [];
  if (Array.isArray(loc.city_ids)) {
    return loc.city_ids.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
  }
  if (typeof loc.city_ids === "string" && String(loc.city_ids).trim() !== "") {
    return String(loc.city_ids)
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter((n) => !Number.isNaN(n));
  }
  if (loc.cities_csv != null && String(loc.cities_csv).trim() !== "") {
    return String(loc.cities_csv)
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter((n) => !Number.isNaN(n));
  }
  const one = num(loc.city_id);
  return one != null ? [one] : [];
}

function zoneLabelFor(zi, loc) {
  const n = zi != null ? zi : 0;
  const name = loc?.zone_name != null ? String(loc.zone_name).trim() : "";
  if (name) return name;
  return n === 0 ? "All zones" : `Zone ${n}`;
}

function stateLabelFor(si, loc) {
  const n = si != null ? si : 0;
  const name =
    (loc?.state_name != null && String(loc.state_name).trim()) ||
    (loc?.state_short_code != null && String(loc.state_short_code).trim()) ||
    "";
  if (name) return name;
  return n === 0 ? "All states" : `State ${n}`;
}

function citySummaryFromRow(cityIds, loc) {
  if (!cityIds.length) return "";
  if (cityIds.length === 1 && cityIds[0] === 0) return "All cities";
  const names = Array.isArray(loc?.city_names) ? loc.city_names.filter((x) => x != null && String(x).trim() !== "") : [];
  if (names.length) return names.join(", ");
  return cityIds.map((id) => (id === 0 ? "All cities" : `City ${id}`)).join(", ");
}

/**
 * One API row → one UI chip / selection.
 */
export function profilingApiRowToSelection(loc) {
  const zi = num(loc.zone_id);
  const si = num(loc.state_id);
  const zone_id = zi != null ? zi : 0;
  const state_id = si != null ? si : 0;
  const city_ids = normalizeCityIdsFromApi(loc);
  if (!city_ids.length) return null;

  const zl = zoneLabelFor(zone_id, loc);
  const sl = stateLabelFor(state_id, loc);
  const cl = citySummaryFromRow(city_ids, loc);

  return {
    kind: LOCATION_GROUP_KIND,
    zone_id,
    state_id,
    city_ids,
    zoneLabel: zl,
    stateLabel: sl,
    cityLabel: cl,
    label: buildGroupLabel(zl, sl, cl)
  };
}

export function profilingApiRowsToSelections(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const loc of rows) {
    const s = profilingApiRowToSelection(loc);
    if (s) out.push(s);
  }
  return out;
}

/**
 * Normalise one row from location-search (`data.list` or typed arrays).
 */
export function normalizeLocationSearchRow(row) {
  if (!row || typeof row !== "object") return null;
  const raw = String(row.bucket || row.type || "").toLowerCase();
  let internal = API_BUCKET_TO_INTERNAL[raw];
  if (!internal) internal = inferBucketFromRow(row);
  if (!internal) return null;

  const label =
    row.label ||
    row.display_label ||
    row.display_name ||
    (internal === "cities"
      ? [row.city_name, row.state_name].filter(Boolean).join(", ")
      : internal === "states"
        ? [row.state_name, row.state_short_code].filter(Boolean).join(" · ")
        : [row.zone_name, row.name].filter(Boolean).join(" · ")) ||
    "";

  let value = null;
  let _idKey = "id";
  let zone_id = num(row.zone_id);
  let state_id = num(row.state_id);

  if (internal === "cities") {
    value = num(row.city_id ?? row.id);
    _idKey = "city_id";
    if (state_id == null) state_id = num(row.state_id);
    if (zone_id == null) zone_id = num(row.zone_id);
  } else if (internal === "states") {
    value = num(row.state_id ?? row.id);
    _idKey = "state_id";
    if (zone_id == null) zone_id = num(row.zone_id);
  } else if (internal === "zones") {
    value = num(row.zone_id ?? row.id);
    _idKey = "zone_id";
    zone_id = value;
    state_id = undefined;
  }

  if (value == null || value === "") return null;

  return {
    ...row,
    label: label || String(value),
    _bucket: internal,
    _idKey,
    _kind: INTERNAL_KIND_LABEL[internal] || raw,
    zone_id: zone_id != null ? zone_id : undefined,
    state_id: state_id != null ? state_id : undefined
  };
}

export function flattenLocationSearchResponse(res, { restrictBucket } = {}) {
  const data = res?.data != null && typeof res.data === "object" ? res.data : res || {};
  let list = [];
  if (Array.isArray(data.list)) {
    list = [...data.list];
  } else {
    for (const row of data.cities || []) list.push({ ...row, bucket: row.bucket || "city" });
    for (const row of data.states || []) list.push({ ...row, bucket: row.bucket || "state" });
    for (const row of data.zones || []) list.push({ ...row, bucket: row.bucket || "zone" });
  }
  const out = [];
  for (const row of list) {
    const n = normalizeLocationSearchRow(row);
    if (!n) continue;
    if (restrictBucket && n._bucket !== restrictBucket) continue;
    out.push(n);
  }
  return out;
}

function findHitForBucketValue(flatHits, bucket, value) {
  const idKey = bucket === "cities" ? "city_id" : bucket === "states" ? "state_id" : "zone_id";
  return flatHits.find((h) => {
    if (h._bucket !== bucket) return false;
    const idVal = h[idKey] ?? h[h._idKey];
    if (idVal == null || idVal === "") return false;
    return String(Number(idVal)) === String(Number(value));
  });
}

function singleNeedsHydration(s) {
  if (!s || s.bucket == null) return false;
  const val = String(s.value).trim();
  const lab = s.label != null ? String(s.label).trim() : "";
  if (!lab || lab === val) return true;
  if (/^(City|State|Zone):\s*.+$/i.test(lab)) return true;
  return false;
}

function hydrateParamsForBucket(bucket, value) {
  if (bucket === "cities") {
    return { types: "cities", city_id: num(value), search_text: String(value) };
  }
  if (bucket === "states") {
    return { types: "states", state_id: num(value), search_text: String(value) };
  }
  if (bucket === "zones") {
    return { types: "zones", zone_id: num(value), search_text: String(value) };
  }
  return { search_text: String(value), page_size: 40, types: "cities,states,zones" };
}

function groupNeedsHydration(g) {
  if (!isLocationGroup(g)) return false;
  const zl = g.zoneLabel != null ? String(g.zoneLabel).trim() : "";
  const sl = g.stateLabel != null ? String(g.stateLabel).trim() : "";
  const cl = g.cityLabel != null ? String(g.cityLabel).trim() : "";
  const zi = num(g.zone_id);
  const si = num(g.state_id);
  const cids = Array.isArray(g.city_ids) ? g.city_ids : g.city_id != null ? [num(g.city_id)] : [];

  if (zi != null && zl && (zl === `Zone ${zi}` || zl === "All zones")) {
    if (zi !== 0 && zl === `Zone ${zi}`) return true;
  }
  if (si != null && sl && (sl === `State ${si}` || sl === "All states")) {
    if (si !== 0 && sl === `State ${si}`) return true;
  }
  if (cids.length && !(cids.length === 1 && cids[0] === 0)) {
    if (!cl || /^City\s+\d+(\s*,\s*City\s+\d+)*$/i.test(cl)) return true;
  }
  return false;
}

async function hydrateOneLabel(bucket, value) {
  const base = hydrateParamsForBucket(bucket, value);
  const res = await searchFreightLocations({ ...base, page_number: 1, page_size: 40 });
  const flat = flattenLocationSearchResponse(res);
  const hit = findHitForBucketValue(flat, bucket, value);
  return hit?.label || null;
}

/**
 * Resolve labels for {@link LOCATION_GROUP_KIND} rows.
 */
export async function hydrateProfilingSelectionLabels(selections) {
  if (!selections?.length) return selections;
  const out = selections.map((s) => ({ ...s }));

  const tasks = out.map((s, idx) => {
    if (isLocationGroup(s)) {
      if (!groupNeedsHydration(s)) return Promise.resolve({ idx, kind: "skip" });
      const zi = num(s.zone_id);
      const si = num(s.state_id);
      const cids = Array.isArray(s.city_ids) ? [...s.city_ids] : s.city_id != null ? [num(s.city_id)] : [];
      return Promise.all([
        zi != null && zi !== 0 ? hydrateOneLabel("zones", zi).catch(() => null) : Promise.resolve(null),
        si != null && si !== 0 ? hydrateOneLabel("states", si).catch(() => null) : Promise.resolve(null),
        !(cids.length === 1 && cids[0] === 0)
          ? Promise.all(
              cids.map((cid) =>
                cid != null && cid !== 0 ? hydrateOneLabel("cities", cid).catch(() => null) : Promise.resolve(null)
              )
            )
          : Promise.resolve([])
      ]).then(([zl, sl, cityLabs]) => ({ idx, kind: "group", zl, sl, cityLabs }));
    }
    if (s.bucket && singleNeedsHydration(s)) {
      return hydrateOneLabel(s.bucket, s.value)
        .then((label) => ({ idx, kind: "single", label }))
        .catch(() => ({ idx, kind: "single", label: null }));
    }
    return Promise.resolve({ idx, kind: "skip" });
  });

  const settled = await Promise.all(tasks);
  settled.forEach((r) => {
    if (!r || r.kind === "skip") return;
    if (r.kind === "single" && r.label) {
      out[r.idx] = { ...out[r.idx], label: r.label };
      return;
    }
    if (r.kind === "group") {
      const cur = out[r.idx];
      if (!isLocationGroup(cur)) return;
      const zoneLabel = r.zl != null ? r.zl : cur.zoneLabel;
      const stateLabel = r.sl != null ? r.sl : cur.stateLabel;
      const cids = Array.isArray(cur.city_ids) ? cur.city_ids : cur.city_id != null ? [num(cur.city_id)] : [];
      let cityLabel = cur.cityLabel;
      if (cids.length === 1 && cids[0] === 0) {
        cityLabel = "All cities";
      } else if (Array.isArray(r.cityLabs) && r.cityLabs.length) {
        const parts = r.cityLabs.map((lab, i) => lab || (cids[i] != null ? `City ${cids[i]}` : "")).filter(Boolean);
        if (parts.length) cityLabel = parts.join(", ");
      }
      const label = buildGroupLabel(zoneLabel, stateLabel, cityLabel);
      out[r.idx] = {
        ...cur,
        zoneLabel,
        stateLabel,
        cityLabel,
        label: label || cur.label
      };
    }
  });

  return out;
}

const TIER_META = {
  zone: { step: 1, icon: "fa-layer-group", placeholder: "Type zone name…" },
  state: { step: 2, icon: "fa-map-marked-alt", placeholder: "Type state name…" },
  city: { step: 3, icon: "fa-building", placeholder: "Type city name…" }
};

function TierTypeahead({ label, hint, tier, filterZoneId, filterStateId, onPick, variant }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState([]);
  const innerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (innerRef.current && !innerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!debounced) {
      setHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const types = typesParamForTier(tier);
        const body = {
          search_text: debounced,
          page_number: 1,
          page_size: 50,
          types
        };
        if (tier !== "zone" && filterZoneId != null) body.zone_id = filterZoneId;
        if (tier === "city" && filterStateId != null) body.state_id = filterStateId;
        const res = await searchFreightLocations(body);
        const rb = tier === "zone" ? "zones" : tier === "state" ? "states" : "cities";
        if (!cancelled) setHits(flattenLocationSearchResponse(res, { restrictBucket: rb }));
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, tier, filterZoneId, filterStateId]);

  const showPanel = open && (loading || debounced.length > 0 || hits.length > 0);
  const meta = TIER_META[tier] || TIER_META.zone;
  const v = variant === "unloading" ? "unloading" : "loading";

  return (
    <div
      className={`freight-prof-tier freight-prof-tier--${tier} freight-prof-tier--${v} min-w-0`}
      ref={innerRef}
    >
      <div className="freight-prof-tier__rail" aria-hidden>
        <span className="freight-prof-tier__step">{meta.step}</span>
        <span className="freight-prof-tier__connector" />
      </div>
      <div className="freight-prof-tier__body min-w-0">
        <div className="freight-prof-tier__title-row">
          <span className={`freight-prof-tier__icon-badge freight-prof-tier__icon-badge--${tier}`}>
            <i className={`fas ${meta.icon}`} aria-hidden />
          </span>
          <div className="freight-prof-tier__titles min-w-0">
            <span className="freight-prof-tier__label">{label}</span>
            {hint ? (
              <span className="freight-prof-tier__hint truncate block max-w-full" title={hint}>
                {hint}
              </span>
            ) : null}
          </div>
        </div>
        <div className="freight-prof-tier__search-wrap">
          
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={meta.placeholder}
            className="freight-prof-tier__search"
            autoComplete="off"
            aria-label={`Search ${label}`}
          />
          {showPanel && (
            <div className="freight-prof-tier__dropdown z-20">
              {loading && <div className="freight-prof-tier__dd-muted">Searching…</div>}
              {!loading && debounced && hits.length === 0 && (
                <div className="freight-prof-tier__dd-muted">No matches</div>
              )}
              {!loading &&
                hits.map((h, i) => (
                  <button
                    key={`${h._bucket}-${i}-${h.label}`}
                    type="button"
                    title={h.label}
                    className="freight-prof-tier__dd-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onPick(h);
                      setQ("");
                      setOpen(false);
                      setHits([]);
                      setDebounced("");
                    }}
                  >
                    <span className="freight-prof-tier__dd-label">{h.label}</span>
                    <span className="freight-prof-tier__dd-kind">{h._kind}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function slotLabel(slot) {
  if (!slot) return "—";
  return slot.label || String(slot.id);
}

function rowFingerprint(g) {
  if (!isLocationGroup(g)) return "";
  const zi = num(g.zone_id) ?? 0;
  const si = num(g.state_id) ?? 0;
  const cids = Array.isArray(g.city_ids) ? [...g.city_ids].map(Number).sort((a, b) => a - b).join(",") : "";
  return `${zi}|${si}|${cids}`;
}

function selectionCityIds(g) {
  if (!isLocationGroup(g)) return [];
  if (Array.isArray(g.city_ids) && g.city_ids.length) return g.city_ids.map((x) => Number(x));
  const c = num(g.city_id);
  return c != null ? [c] : [];
}

/**
 * Build `profilingStore` side payload: `{ rows: [ { zone_id, state_id, city_ids } ] }`.
 */
export function selectionsToProfilingRows(selections) {
  const rows = [];
  for (const s of selections || []) {
    if (!isLocationGroup(s)) continue;
    const zi = num(s.zone_id);
    const si = num(s.state_id);
    const zone_id = zi != null ? zi : 0;
    const state_id = si != null ? si : 0;
    const city_ids = selectionCityIds(s)
      .map((n) => Number(n))
      .filter((n) => !Number.isNaN(n));
    if (!city_ids.length) continue;
    rows.push({ zone_id, state_id, city_ids });
  }
  return rows;
}

/** @deprecated Prefer {@link selectionsToProfilingRows}. Legacy parallel arrays for old servers. */
export function selectionsToBuckets(selections) {
  const rows = selectionsToProfilingRows(selections);
  const cities = [];
  const states = [];
  const zones = [];
  for (const r of rows) {
    for (const cid of r.city_ids) {
      zones.push(r.zone_id);
      states.push(r.state_id);
      cities.push(cid);
    }
  }
  return { cities, states, zones };
}

function arrAt(arr, i) {
  if (!Array.isArray(arr) || i < 0 || i >= arr.length) return undefined;
  return arr[i];
}

/**
 * Legacy GET: parallel `zones[i]`, `states[i]`, `cities[i]`.
 */
export function parallelTripleArraysToGroupSelections(side) {
  if (!side || typeof side !== "object") return [];
  const z = side.zones || [];
  const st = side.states || [];
  const c = side.cities || [];
  const n = Math.max(z.length, st.length, c.length);
  const out = [];
  for (let i = 0; i < n; i++) {
    const zi = num(arrAt(z, i));
    const si = num(arrAt(st, i));
    const ci = num(arrAt(c, i));
    if (zi == null || si == null || ci == null) continue;
    const loc = {
      zone_id: zi,
      state_id: si,
      city_ids: [ci],
      zone_name: "",
      state_name: "",
      city_names: []
    };
    const sel = profilingApiRowToSelection(loc);
    if (sel) out.push(sel);
  }
  return out;
}

/**
 * @deprecated Use {@link profilingApiRowsToSelections} with `rows` or `locations` from GET.
 */
export function locationsRowsToGroupSelections(locations) {
  return profilingApiRowsToSelections(locations);
}

/** @deprecated Use {@link parallelTripleArraysToGroupSelections}. */
export function bucketsToSelections(buckets) {
  return parallelTripleArraysToGroupSelections(buckets || {});
}

/**
 * Prefer `rows` / `locations` from API; else legacy parallel arrays.
 */
export function profilingSideToSelections(side) {
  if (!side || typeof side !== "object") return [];
  const rowSrc =
    Array.isArray(side.rows) && side.rows.length > 0
      ? side.rows
      : Array.isArray(side.locations) && side.locations.length > 0
        ? side.locations
        : null;
  if (rowSrc) {
    return profilingApiRowsToSelections(rowSrc);
  }
  const z = side.zones;
  const st = side.states;
  const c = side.cities;
  const hasParallel =
    (Array.isArray(z) && z.length > 0) ||
    (Array.isArray(st) && st.length > 0) ||
    (Array.isArray(c) && c.length > 0);
  if (hasParallel) {
    return parallelTripleArraysToGroupSelections(side);
  }
  return [];
}

const FreightProfilingLocationCombo = ({ title, subtitle, selections, onChange, variant = "loading" }) => {
  const emptyDraft = () => ({
    zone: null,
    state: null,
    cityPicks: [],
    allCitiesInState: false,
    wholeZone: false
  });
  const [draft, setDraft] = useState(emptyDraft);

  const clearDraft = () => setDraft(emptyDraft());

  const filterZoneId = draft.zone?.id != null && draft.zone.id !== 0 ? draft.zone.id : null;
  const filterStateId = draft.state?.id != null && draft.state.id !== 0 ? draft.state.id : null;

  const pushRow = (rowSel) => {
    const fp = rowFingerprint(rowSel);
    const dup = selections.some((g) => isLocationGroup(g) && rowFingerprint(g) === fp);
    if (dup) {
      clearDraft();
      return;
    }
    onChange([...selections, rowSel]);
    clearDraft();
  };

  const tryCommitRow = () => {
    if (draft.wholeZone && draft.zone != null) {
      const zi = num(draft.zone.id) ?? 0;
      const zl = draft.zone.label || zoneLabelFor(zi, {});
      const rowSel = profilingApiRowToSelection({
        zone_id: zi,
        state_id: 0,
        city_ids: [0],
        zone_name: zl,
        state_name: "All states",
        city_names: ["All cities"]
      });
      if (rowSel) pushRow(rowSel);
      return;
    }
    if (draft.allCitiesInState && draft.zone != null && draft.state != null) {
      const zi = num(draft.zone.id) ?? 0;
      const si = num(draft.state.id) ?? 0;
      const rowSel = profilingApiRowToSelection({
        zone_id: zi,
        state_id: si,
        city_ids: [0],
        zone_name: draft.zone.label,
        state_name: draft.state.label,
        city_names: ["All cities"]
      });
      if (rowSel) pushRow(rowSel);
      return;
    }
    const zi = num(draft.zone?.id) ?? 0;
    const si = num(draft.state?.id) ?? 0;
    if (!draft.cityPicks.length) return;
    const city_ids = draft.cityPicks.map((p) => num(p.id)).filter((n) => n != null && !Number.isNaN(n));
    if (!city_ids.length) return;
    const names = draft.cityPicks.map((p) => p.label || `City ${p.id}`);
    const rowSel = profilingApiRowToSelection({
      zone_id: zi,
      state_id: si,
      city_ids,
      zone_name: draft.zone?.label,
      state_name: draft.state?.label,
      city_names: names
    });
    if (rowSel) pushRow(rowSel);
  };

  const onPickZone = (hit) => {
    const zid = num(hit.zone_id ?? hit[hit._idKey]);
    if (zid == null) return;
    setDraft({
      zone: { id: zid, label: hit.label || `Zone ${zid}` },
      state: null,
      cityPicks: [],
      allCitiesInState: false,
      wholeZone: false
    });
  };

  const onPickState = (hit) => {
    const sid = num(hit.state_id ?? hit[hit._idKey]);
    if (sid == null) return;
    const zidFromHit = num(hit.zone_id);
    setDraft((prev) => ({
      zone:
        zidFromHit != null
          ? {
              id: zidFromHit,
              label: hit.zone_name ? String(hit.zone_name) : prev.zone?.label || `Zone ${zidFromHit}`
            }
          : prev.zone,
      state: { id: sid, label: hit.label || `State ${sid}` },
      cityPicks: [],
      allCitiesInState: false,
      wholeZone: false
    }));
  };

  const onPickCity = (hit) => {
    const ci = num(hit.city_id ?? hit[hit._idKey]);
    if (ci == null) return;
    const ziHit = num(hit.zone_id) ?? 0;
    const siHit = num(hit.state_id) ?? 0;
    const zoneObj =
      ziHit != null
        ? {
            id: ziHit,
            label: hit.zone_name ? String(hit.zone_name) : draft.zone?.label || (ziHit === 0 ? "All zones" : `Zone ${ziHit}`)
          }
        : draft.zone || { id: 0, label: "All zones" };
    const stateObj =
      siHit != null
        ? {
            id: siHit,
            label: hit.state_name ? String(hit.state_name) : draft.state?.label || `State ${siHit}`
          }
        : draft.state;
    if (!stateObj || stateObj.id == null) {
      return;
    }
    const cityEntry = { id: ci, label: hit.label || `City ${ci}` };
    setDraft((prev) => {
      const base = {
        zone: zoneObj,
        state: stateObj,
        cityPicks: [...prev.cityPicks],
        allCitiesInState: false,
        wholeZone: false
      };
      if (base.cityPicks.some((p) => Number(p.id) === Number(ci))) return base;
      base.cityPicks.push(cityEntry);
      return base;
    });
  };

  const removeCityPick = (id) => {
    setDraft((d) => ({
      ...d,
      cityPicks: d.cityPicks.filter((p) => Number(p.id) !== Number(id))
    }));
  };

  const v = variant === "unloading" ? "unloading" : "loading";
  const stateHint =
    filterZoneId != null ? `Filtered by zone: ${draft.zone?.label || filterZoneId}` : "Pick a zone first, or search a city (fills zone & state when returned).";
  const cityHint =
    filterZoneId != null || filterStateId != null
      ? [filterZoneId != null ? `Zone: ${draft.zone?.label || filterZoneId}` : null, filterStateId != null ? `State: ${draft.state?.label || filterStateId}` : null]
          .filter(Boolean)
          .join(" · ")
      : "Set zone & state, then add one or more cities to this row — or use shortcuts below.";

  const groupRows = selections.filter(isLocationGroup);

  const removeGroup = (g) => {
    const fp = rowFingerprint(g);
    onChange(selections.filter((s) => !isLocationGroup(s) || rowFingerprint(s) !== fp));
  };

  const showWholeZoneToggle = draft.zone != null;
  const showAllCitiesToggle = draft.zone != null && draft.state != null && !draft.wholeZone;
  const canCommitCities =
    draft.zone != null &&
    draft.state != null &&
    draft.cityPicks.length > 0 &&
    !draft.wholeZone &&
    !draft.allCitiesInState;
  const canCommitAllCities = draft.allCitiesInState && draft.zone && draft.state;
  const canCommitWholeZone = draft.wholeZone && draft.zone;

  const addRowDisabled = !(canCommitCities || canCommitAllCities || canCommitWholeZone);

  return (
    <div className={`freight-prof-location freight-prof-location--${v} flex min-w-0 flex-col gap-3 sm:gap-4`}>
      <div className="freight-prof-location__head shrink-0">
        <h4 className="freight-prof-location__title">{title}</h4>
        {subtitle ? <p className="freight-prof-location__sub text-slate-600 text-sm leading-snug">{subtitle}</p> : null}
      </div>

      <div className="freight-prof-loc-selected min-w-0">
        <div className="freight-prof-loc-selected__head">
          <span className="freight-prof-loc-selected__title">
            <i className="fas fa-map-pin freight-prof-loc-selected__title-icon" aria-hidden />
            Location rules
          </span>
          <span className="freight-prof-loc-selected__count">{groupRows.length}</span>
        </div>
        <div className="freight-prof-location__chips freight-prof-location__chips--boxed flex flex-wrap gap-2 content-start min-h-[3rem]">
          {groupRows.length === 0 ? (
            <span className="freight-prof-location__empty text-sm text-slate-500">
              No rules yet — add rows below. Each row is one saved rule: zone, state, and cities (or “all cities” /
              “whole zone” shortcuts).
            </span>
          ) : (
            groupRows.map((s, i) => (
              <span
                key={`g-${rowFingerprint(s)}-${i}`}
                className="freight-prof-chip freight-prof-chip--group inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left"
              >
                <span className="freight-prof-chip__kind shrink-0">Row</span>
                <span className="freight-prof-chip__label min-w-0 truncate text-sm font-medium" title={s.label}>
                  {s.label}
                </span>
                <button
                  type="button"
                  className="freight-prof-chip__remove shrink-0 rounded p-0.5 hover:bg-slate-200/80"
                  title="Remove row"
                  aria-label={`Remove ${s.label}`}
                  onClick={() => removeGroup(s)}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      <div className="freight-prof-draft rounded-xl border border-slate-200 bg-slate-50/90 p-3 sm:p-4">
        <div className="freight-prof-draft__title">
          <i className="fas fa-pen-fancy" aria-hidden />
          Draft rule
        </div>
        <div className="freight-prof-draft__slots">
          <div className="freight-prof-draft__slot">
            <span className="freight-prof-draft__slot-k">Zone</span>
            <span className="freight-prof-draft__slot-v">{slotLabel(draft.zone)}</span>
          </div>
          <span className="freight-prof-draft__arrow" aria-hidden>
            →
          </span>
          <div className="freight-prof-draft__slot">
            <span className="freight-prof-draft__slot-k">State</span>
            <span className="freight-prof-draft__slot-v">{draft.wholeZone ? "—" : slotLabel(draft.state)}</span>
          </div>
          <span className="freight-prof-draft__arrow" aria-hidden>
            →
          </span>
          <div className="freight-prof-draft__slot min-w-[6rem] flex-1">
            <span className="freight-prof-draft__slot-k">Cities</span>
            <span className="freight-prof-draft__slot-v text-sm">
              {draft.wholeZone ? (
                "Whole zone"
              ) : draft.allCitiesInState ? (
                "All cities in state"
              ) : draft.cityPicks.length ? (
                <span className="flex flex-wrap gap-1">
                  {draft.cityPicks.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-red-50"
                      title="Remove city"
                      onClick={() => removeCityPick(p.id)}
                    >
                      {p.label}
                      <span className="text-slate-400">×</span>
                    </button>
                  ))}
                </span>
              ) : (
                "—"
              )}
            </span>
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {showWholeZoneToggle ? (
            <button
              type="button"
              className={`rounded-lg border px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                draft.wholeZone
                  ? "border-amber-500 bg-amber-50 text-amber-900"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  wholeZone: !d.wholeZone,
                  allCitiesInState: false,
                  state: d.wholeZone ? d.state : null,
                  cityPicks: []
                }))
              }
            >
              {draft.wholeZone ? "Whole zone (on) — tap to cancel" : "Whole zone: all states & cities"}
            </button>
          ) : null}
          {showAllCitiesToggle ? (
            <button
              type="button"
              className={`rounded-lg border px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                draft.allCitiesInState
                  ? "border-sky-500 bg-sky-50 text-sky-900"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
              disabled={draft.cityPicks.length > 0 && !draft.allCitiesInState}
              title={
                draft.cityPicks.length && !draft.allCitiesInState
                  ? "Remove cities first to use this shortcut"
                  : undefined
              }
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  allCitiesInState: !d.allCitiesInState,
                  cityPicks: d.allCitiesInState ? d.cityPicks : []
                }))
              }
            >
              {draft.allCitiesInState ? "All cities in state (on) — tap to cancel" : "All cities in this state"}
            </button>
          ) : null}
        </div>

        {!draft.zone ? (
          <p className="freight-prof-draft__hint mt-2">Pick a zone, or search a city first if results include zone and state.</p>
        ) : draft.wholeZone ? (
          <p className="freight-prof-draft__ready mt-2">Adds one row: this zone, all states, all cities (`state_id: 0`, `city_ids: [0]`).</p>
        ) : !draft.state ? (
          <p className="freight-prof-draft__hint mt-2">Pick a state (filtered by zone when set), or pick a city to fill state.</p>
        ) : draft.allCitiesInState ? (
          <p className="freight-prof-draft__ready mt-2">Adds one row covering every city in this state.</p>
        ) : (
          <p className="freight-prof-draft__ready mt-2">Search cities below — add several to one row, then click “Add row”.</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={addRowDisabled}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={tryCommitRow}
          >
            Add row to list
          </button>
          <button type="button" className="freight-prof-draft__clear rounded-lg" onClick={clearDraft}>
            Clear draft
          </button>
        </div>
      </div>

      <div className="freight-prof-loc-add min-w-0">
        <div className="freight-prof-loc-add__header">
          <span className="freight-prof-loc-add__header-icon" aria-hidden>
            <i className="fas fa-search-plus" />
          </span>
          <div className="min-w-0">
            <span className="freight-prof-loc-add__header-title">Search & pick</span>
            <span className="freight-prof-loc-add__header-sub block text-slate-600">
              Typeahead uses <code className="text-xs">/freight-profiling/location-search</code>. Saved as{" "}
              <code className="text-xs">rows[]</code> with <code className="text-xs">zone_id</code>,{" "}
              <code className="text-xs">state_id</code>, <code className="text-xs">city_ids</code> (0 means “all” where
              allowed).
            </span>
          </div>
        </div>
        <div className="freight-prof-tier-stack space-y-1">
          <TierTypeahead
            label="Zone"
            hint="Pick a zone, or skip and choose a city if it carries zone & state."
            tier="zone"
            variant={v}
            filterZoneId={null}
            filterStateId={null}
            onPick={onPickZone}
          />
          <TierTypeahead
            label="State"
            hint={stateHint}
            tier="state"
            variant={v}
            filterZoneId={filterZoneId}
            filterStateId={null}
            onPick={onPickState}
          />
          <TierTypeahead
            label="City"
            hint={cityHint}
            tier="city"
            variant={v}
            filterZoneId={filterZoneId}
            filterStateId={filterStateId}
            onPick={onPickCity}
          />
        </div>
      </div>
    </div>
  );
};

export default FreightProfilingLocationCombo;

/**
 * @deprecated Prefer {@link profilingApiRowsToSelections}.
 */
export function locationsToSelections(locations) {
  return profilingApiRowsToSelections(locations);
}
